import React, { useEffect, useState } from "react";
import Layout from "./layout";
import { useParams, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { doFetch, doPost, clearPublicTourEvents } from "../redux/api/actions";
import { GET_TOUR, GET_PUBLIC_TOUR_EVENTS, CREATE_BOOKING } from "../redux/api/constants";
import { displayCostType } from "./utils";

const TourBookingCard = ({ tour, events, isLoggedIn, onBook }) => {
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [quantity, setQuantity] = useState(1);
    const [showSuccess, setShowSuccess] = useState(false);
    const lastBooking = useSelector(state => state.API.lastBooking);
    const loading = useSelector(state => state.API.loading);

    useEffect(() => {
        if (lastBooking) {
            setShowSuccess(true);
            setSelectedEvent(null);
        }
    }, [lastBooking]);

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric'
        });
    };

    const formatTime = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getAvailableSpots = (event) => {
        return event.total_capacity - event.total_booked;
    };

    const handleBook = () => {
        if (selectedEvent && quantity > 0) {
            onBook(selectedEvent.id, quantity);
        }
    };

    const totalPrice = tour.cost_type === 'PER_PERSON' 
        ? tour.cost * quantity 
        : tour.cost;

    if (showSuccess) {
        return (
            <div className="booking-card">
                <div className="booking-success text-center py-4">
                    <i className="bi bi-check-circle-fill text-success" style={{ fontSize: '3rem' }}></i>
                    <h4 className="mt-3">Booking Confirmed!</h4>
                    <p className="text-muted">You're all set. The tour guide will be in touch with more details.</p>
                    <button 
                        className="btn-outline mt-3"
                        onClick={() => setShowSuccess(false)}
                    >
                        Book Another Date
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="booking-card">
            <div className="booking-header">
                <div className="price-display">
                    <span className="price">${tour.cost}</span>
                    <span className="price-type">/ {displayCostType(tour.cost_type)}</span>
                </div>
            </div>

            {events && events.length > 0 ? (
                <>
                    <div className="booking-section">
                        <label className="booking-label">Select a Date</label>
                        <div className="date-options">
                            {events.map((event) => {
                                const available = getAvailableSpots(event);
                                return (
                                    <button
                                        key={event.id}
                                        className={`date-option ${selectedEvent?.id === event.id ? 'selected' : ''}`}
                                        onClick={() => setSelectedEvent(event)}
                                        disabled={available === 0}
                                    >
                                        <div className="date-info">
                                            <span className="date">{formatDate(event.tour_date)}</span>
                                            <span className="time">{formatTime(event.tour_date)}</span>
                                        </div>
                                        <span className={`spots ${available <= 3 ? 'low' : ''}`}>
                                            {available === 0 ? 'Full' : `${available} spots`}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {selectedEvent && (
                        <div className="booking-section">
                            <label className="booking-label">Number of Guests</label>
                            <div className="quantity-selector">
                                <button 
                                    className="qty-btn"
                                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                                    disabled={quantity <= 1}
                                >
                                    <i className="bi bi-dash"></i>
                                </button>
                                <span className="qty-value">{quantity}</span>
                                <button 
                                    className="qty-btn"
                                    onClick={() => setQuantity(Math.min(getAvailableSpots(selectedEvent), quantity + 1))}
                                    disabled={quantity >= getAvailableSpots(selectedEvent)}
                                >
                                    <i className="bi bi-plus"></i>
                                </button>
                            </div>
                        </div>
                    )}

                    {selectedEvent && (
                        <div className="booking-total">
                            <span>Total</span>
                            <span className="total-price">${totalPrice.toFixed(2)}</span>
                        </div>
                    )}

                    {isLoggedIn ? (
                        <button 
                            className="btn-primary booking-btn"
                            onClick={handleBook}
                            disabled={!selectedEvent || loading}
                        >
                            {loading ? 'Booking...' : 'Book Now'}
                        </button>
                    ) : (
                        <div className="login-prompt">
                            <p className="text-muted mb-2">Sign in to book this tour</p>
                            <Link to="/signin" className="btn-primary booking-btn">
                                Sign In to Book
                            </Link>
                        </div>
                    )}
                </>
            ) : (
                <div className="no-dates">
                    <i className="bi bi-calendar-x"></i>
                    <p>No available dates</p>
                    <span className="text-muted small">Check back soon for new tour dates!</span>
                </div>
            )}
        </div>
    );
};

const Tour = () => {
    const { tourId } = useParams();
    const tour = useSelector(state => state.API.tour);
    const publicTourEvents = useSelector(state => state.API.publicTourEvents);
    const accessToken = useSelector(state => state.Auth.accessToken);
    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(doFetch(GET_TOUR, `tours/${tourId}`, null));
        dispatch(doFetch(GET_PUBLIC_TOUR_EVENTS, `tours/${tourId}/events`, null));
        
        return () => {
            dispatch(clearPublicTourEvents());
        };
    }, [tourId, dispatch]);

    const handleBook = (eventId, quantity) => {
        dispatch(doPost(
            CREATE_BOOKING,
            `tours/${tourId}/book`,
            accessToken,
            { tour_event_id: eventId, quantity }
        ));
    };

    const imageUrls = tour?.image_urls ? tour.image_urls.split(",").filter(url => url.trim()) : [];
    const [activeIndex, setActiveIndex] = React.useState(0);

    const updateActiveIndex = (increment) => {
        if (imageUrls.length === 0) return;
        const maxIndex = imageUrls.length - 1;
        let newIndex = activeIndex + increment;
        if (newIndex < 0) newIndex = maxIndex;
        else if (newIndex > maxIndex) newIndex = 0;
        setActiveIndex(newIndex);
    };

    return (
        <Layout selected="tour">
            {tour && (
                <div className="tour-page">
                    <nav aria-label="breadcrumb" className="breadcrumb-nav">
                        <ol className="breadcrumb">
                            <li className="breadcrumb-item">
                                <Link to="/">Home</Link>
                            </li>
                            <li className="breadcrumb-item active">{tour.title}</li>
                        </ol>
                    </nav>

                    <div className="tour-layout">
                        <div className="tour-main">
                            {imageUrls.length > 0 && (
                                <div className="tour-gallery mb-4">
                                    <div className="carousel slide">
                                        <div className="carousel-inner rounded">
                                            {imageUrls.map((imageUrl, index) => (
                                                <div 
                                                    className={index === activeIndex ? "carousel-item active" : "carousel-item"} 
                                                    key={index}
                                                >
                                                    <img 
                                                        src={imageUrl} 
                                                        className="d-block w-100"
                                                        style={{ maxHeight: '450px', objectFit: 'cover' }}
                                                        alt={`${tour.title} - ${index + 1}`} 
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                        {imageUrls.length > 1 && (
                                            <>
                                                <button className="carousel-control-prev" type="button" onClick={() => updateActiveIndex(-1)}>
                                                    <span className="carousel-control-prev-icon" aria-hidden="true"></span>
                                                </button>
                                                <button className="carousel-control-next" type="button" onClick={() => updateActiveIndex(1)}>
                                                    <span className="carousel-control-next-icon" aria-hidden="true"></span>
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            )}

                            <h1 className="tour-title">{tour.title}</h1>
                            
                            <div className="tour-meta">
                                <span className="meta-item">
                                    <i className="bi bi-clock"></i> {tour.duration_in_hours} hours
                                </span>
                                <span className="meta-item">
                                    <i className="bi bi-people"></i> Up to {tour.max_tour_size} guests
                                </span>
                            </div>

                            <div className="tour-description">
                                <h3>About This Tour</h3>
                                <p>{tour.description}</p>
                            </div>

                            {tour.tourguide && (
                                <div className="host-section">
                                    <h3>Your Host</h3>
                                    <div className="host-card">
                                        {tour.tourguide.avatar_url && (
                                            <img 
                                                src={tour.tourguide.avatar_url} 
                                                alt={tour.tourguide.name}
                                                className="host-avatar"
                                            />
                                        )}
                                        <div className="host-info">
                                            <h4>{tour.tourguide.name}</h4>
                                            {tour.tourguide.bio && (
                                                <p className="text-muted">{tour.tourguide.bio}</p>
                                            )}
                                            <div className="host-contact">
                                                {tour.tourguide.email && (
                                                    <a href={`mailto:${tour.tourguide.email}`}>
                                                        <i className="bi bi-envelope"></i>
                                                    </a>
                                                )}
                                                {tour.tourguide.facebook && (
                                                    <a href={tour.tourguide.facebook} target="_blank" rel="noopener noreferrer">
                                                        <i className="bi bi-facebook"></i>
                                                    </a>
                                                )}
                                                {tour.tourguide.instagram && (
                                                    <a href={tour.tourguide.instagram} target="_blank" rel="noopener noreferrer">
                                                        <i className="bi bi-instagram"></i>
                                                    </a>
                                                )}
                                                {tour.tourguide.webpage && (
                                                    <a href={tour.tourguide.webpage} target="_blank" rel="noopener noreferrer">
                                                        <i className="bi bi-globe"></i>
                                                    </a>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="tour-sidebar">
                            <TourBookingCard 
                                tour={tour}
                                events={publicTourEvents}
                                isLoggedIn={!!accessToken}
                                onBook={handleBook}
                            />
                        </div>
                    </div>
                </div>
            )}
        </Layout>
    );
};

export default Tour;
